import { SxProps } from '@mui/system';
import {
  MultimediaManager2,
  NotificationTypeEnum,
  OneMedia,
  notificationQueue,
  useNotification,
} from '@prismamedia/one-components';
import { move } from 'ramda';
import { Dispatch, FC, SetStateAction, useState } from 'react';
import { useUploadImage } from 'utils/useUploadImage';
import { MediaFields } from './MediaFields';

export const MediaInput: FC<{
  medias: OneMedia[];
  setMedias: Dispatch<SetStateAction<OneMedia[]>>;
  brandKey: string;
  sx?: SxProps;
  disableFields?: boolean;
  formats?: { label: string; value: string }[];
}> = ({ medias, setMedias, brandKey, sx, disableFields, formats }) => {
  const [selectedMediaIndex, setSelectedMediaIndex] = useState(0);
  const [loading, setLoading] = useState(false);
  const { pushNotification, removeNotification } = useNotification();
  const uploadImage = useUploadImage();

  const selectedMedia = medias[selectedMediaIndex];

  const updateSelectedMedia: Dispatch<SetStateAction<OneMedia>> = (updater) => {
    setMedias((prev) =>
      prev.map((media, i) => {
        const newMedia =
          typeof updater === 'function' ? updater(media) : updater;
        return i === selectedMediaIndex ? newMedia : media;
      }),
    );
  };

  const onUploadFiles = async (files: File[]) => {
    setLoading(true);
    try {
      const queue = files.map((file) => () =>
        uploadImage(file, brandKey.toLowerCase()),
      );

      const newMedias = await notificationQueue(
        queue,
        pushNotification,
        removeNotification,
        'Importation',
      );

      const lastMediaIndex = medias.length - 1;
      setMedias((prev) => [...prev, ...newMedias]);
      setSelectedMediaIndex(lastMediaIndex + 1);
    } catch (error) {
      pushNotification({
        message: (error as Error).message,
        type: NotificationTypeEnum.error,
      });
    }
    setLoading(false);
  };

  return (
    <MultimediaManager2
      sx={sx}
      medias={medias}
      loading={loading}
      onMediaDelete={(index) =>
        setMedias((prev) => prev.filter((_, i) => i !== index))
      }
      onMediaReorder={(oldIndex, newIndex) =>
        setMedias((prev) => move(oldIndex, newIndex, prev))
      }
      onCropAndFocusPointChange={({ crop, focusPoint }, index) =>
        setMedias((prev) =>
          prev.map((media, i) =>
            i === index ? { ...media, focusPoint, crop } : media,
          ),
        )
      }
      selectedMediaIndex={selectedMediaIndex}
      setSelectedMediaIndex={setSelectedMediaIndex}
      onUploadFiles={onUploadFiles}
      mediaFields={
        !disableFields && (
          <MediaFields
            media={selectedMedia}
            setMedia={updateSelectedMedia}
            formats={formats}
          />
        )
      }
    />
  );
};
